<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import schedulingAPI from '../api/scheduling';

const props = defineProps({
  appointment: {
    type: Object,
    default: null,
  },
  services: {
    type: Array,
    default: () => [],
  },
  professionals: {
    type: Array,
    default: () => [],
  },
  patients: {
    type: Array,
    default: () => [],
  },
  partners: {
    type: Array,
    default: () => [],
  },
  initialData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'saved']);

const isSaving = ref(false);
const isQuickCreating = ref(false);
const errorMessage = ref('');

// Patient Autocomplete & Quick Create State
const patientContainerRef = ref(null);
const patientSearchQuery = ref('');
const showPatientDropdown = ref(false);
const isCreatingNewPatient = ref(false);
const newPatientPhone = ref('');
const newPatientEmail = ref('');

// Date & Time Picker Custom State
const selectedDate = ref('');
const selectedTime = ref('08:00');
const activeShift = ref('morning'); // 'morning' | 'afternoon' | 'night'

const morningTimes = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00'];
const afternoonTimes = ['12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
const nightTimes = ['18:00', '19:00', '20:00', '21:00', '22:00'];

const shiftTimes = computed(() => {
  if (activeShift.value === 'morning') return morningTimes;
  if (activeShift.value === 'afternoon') return afternoonTimes;
  return nightTimes;
});

// Recurrence Days for Package Creation
const availableWeekDays = [
  { key: 'monday', label: 'Seg', full: 'Segunda-feira' },
  { key: 'tuesday', label: 'Ter', full: 'Terça-feira' },
  { key: 'wednesday', label: 'Qua', full: 'Quarta-feira' },
  { key: 'thursday', label: 'Qui', full: 'Quinta-feira' },
  { key: 'friday', label: 'Sex', full: 'Sexta-feira' },
  { key: 'saturday', label: 'Sáb', full: 'Sábado' },
  { key: 'sunday', label: 'Dom', full: 'Domingo' },
];

const selectedRecurrenceDays = ref(['monday', 'wednesday', 'friday']);

const formData = ref({
  id: null,
  scheduling_patient_id: null,
  scheduling_service_id: null,
  scheduling_professional_id: null,
  scheduling_partner_id: null,
  status: 'confirmed',
  session_type: 'recurring', // 'recurring' | 'package' | 'single'
  due_day: 5,
  session_number: 1,
  total_sessions: 10,
  price: 0,
  notes: '',
  prontuario_notes: '',
});

const selectedService = computed(() => {
  return props.services.find(s => Number(s.id) === Number(formData.value.scheduling_service_id));
});

const allowsTrialClass = computed(() => {
  return !!selectedService.value?.allow_trial_class;
});

const isTrialClass = ref(false);

const onToggleTrialClass = () => {
  if (isTrialClass.value) {
    formData.value.session_type = 'single';
  } else {
    if (selectedService.value?.billing_type === 'recurring') {
      formData.value.session_type = 'recurring';
    } else if (selectedService.value?.billing_type === 'package') {
      formData.value.session_type = 'package';
    } else {
      formData.value.session_type = 'single';
    }
  }
};

const serviceDuration = computed(() => {
  return selectedService.value?.duration_minutes || 60;
});

const isTimeSlotAllowedForService = slot => {
  if (!selectedService.value?.custom_schedule_enabled) return true;
  const allowed = selectedService.value.allowed_time_slots || [];
  if (allowed.length === 0) return true;
  return allowed.includes(slot);
};

const isDateAllowedForService = dateStr => {
  if (!selectedService.value?.custom_schedule_enabled) return true;
  const allowedDays = selectedService.value.allowed_days || [];
  if (allowedDays.length === 0) return true;

  if (!dateStr) return true;
  const [y, m, d] = dateStr.split('-').map(Number);
  const dateObj = new Date(y, m - 1, d);
  const dayKeyMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayK = dayKeyMap[dateObj.getDay()];
  return allowedDays.includes(dayK);
};

const isRecurrenceDayAllowed = dayKey => {
  if (!selectedService.value?.custom_schedule_enabled) return true;
  const allowedDays = selectedService.value.allowed_days || [];
  if (allowedDays.length === 0) return true;
  return allowedDays.includes(dayKey);
};

// Computed Start & End Timestamps
const computedEndTimeString = computed(() => {
  if (!selectedDate.value || !selectedTime.value) return '';
  const [hours, minutes] = selectedTime.value.split(':').map(Number);
  const totalMin = hours * 60 + minutes + serviceDuration.value;
  const endH = Math.floor(totalMin / 60) % 24;
  const endM = totalMin % 60;
  return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
});

const formattedDateDisplay = computed(() => {
  if (!selectedDate.value) return '';
  const [y, m, d] = selectedDate.value.split('-').map(Number);
  const dateObj = new Date(y, m - 1, d);
  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(dateObj);
});

const filteredPatients = computed(() => {
  const query = patientSearchQuery.value.toLowerCase().trim();
  if (!query) return props.patients.slice(0, 10);

  return props.patients.filter(
    p =>
      p.name.toLowerCase().includes(query) ||
      (p.phone_number && p.phone_number.includes(query)) ||
      (p.email && p.email.toLowerCase().includes(query))
  );
});

const exactPatientMatch = computed(() => {
  const query = patientSearchQuery.value.toLowerCase().trim();
  if (!query) return null;
  return props.patients.find(p => p.name.toLowerCase().trim() === query);
});

const selectPatient = p => {
  formData.value.scheduling_patient_id = p.id;
  patientSearchQuery.value = p.name;
  isCreatingNewPatient.value = false;
  showPatientDropdown.value = false;
};

const handlePatientInput = () => {
  showPatientDropdown.value = true;
  const match = exactPatientMatch.value;
  if (match) {
    formData.value.scheduling_patient_id = match.id;
    isCreatingNewPatient.value = false;
  } else {
    formData.value.scheduling_patient_id = null;
    isCreatingNewPatient.value = patientSearchQuery.value.trim().length > 0;
  }
};

const handleQuickCreatePatient = async () => {
  const name = patientSearchQuery.value.trim();
  if (!name) return;

  isQuickCreating.value = true;
  errorMessage.value = '';
  try {
    const res = await schedulingAPI.createPatient({
      name: name,
      phone_number: newPatientPhone.value.trim(),
      email: newPatientEmail.value.trim(),
    });

    const created = res.data;
    formData.value.scheduling_patient_id = created.id;
    patientSearchQuery.value = created.name;
    isCreatingNewPatient.value = false;
    showPatientDropdown.value = false;
  } catch (err) {
    // Se já existir ou erro, mantém o nome para salvar junto com agendamento
    isCreatingNewPatient.value = true;
    showPatientDropdown.value = false;
  } finally {
    isQuickCreating.value = false;
  }
};

const toggleRecurrenceDay = dayKey => {
  const idx = selectedRecurrenceDays.value.indexOf(dayKey);
  if (idx > -1) {
    if (selectedRecurrenceDays.value.length > 1) {
      selectedRecurrenceDays.value.splice(idx, 1);
    }
  } else {
    selectedRecurrenceDays.value.push(dayKey);
  }
};

const recurrencePreviewText = computed(() => {
  if (formData.value.session_type === 'single') return '';
  const days = selectedRecurrenceDays.value
    .map(k => availableWeekDays.find(w => w.key === k)?.full)
    .filter(Boolean)
    .join(', ');

  if (formData.value.session_type === 'recurring') {
    return `Vaga fixa semanal garantida às [${days}] no horário ${selectedTime.value}. Vencimento todo dia ${formData.value.due_day || 5}. Na agenda, as aulas das próximas 4 semanas serão projetadas automaticamente.`;
  }

  const total = formData.value.total_sessions || 1;
  return `Serão criadas ${total} sessões às [${days}] no horário ${selectedTime.value} com controle regressivo até a Alta Clínica.`;
});

const filteredProfessionals = computed(() => {
  if (!formData.value.scheduling_service_id) return props.professionals;
  const sId = Number(formData.value.scheduling_service_id);

  return props.professionals.filter(p => {
    if (!p.service_ids || p.service_ids.length === 0) return true;
    return p.service_ids.map(Number).includes(sId);
  });
});

const onServiceChange = () => {
  if (selectedService.value) {
    formData.value.price = selectedService.value.price || 0;
    if (!formData.value.id) {
      if (selectedService.value.billing_type === 'recurring') {
        formData.value.session_type = 'recurring';
      } else if (selectedService.value.billing_type === 'single') {
        formData.value.session_type = 'single';
      } else {
        formData.value.session_type = 'package';
      }
    }
  }

  // Auto-ajusta o profissional conforme os que estão habilitados para o serviço
  const validProfIds = filteredProfessionals.value.map(p => p.id);
  if (formData.value.scheduling_professional_id && !validProfIds.includes(formData.value.scheduling_professional_id)) {
    formData.value.scheduling_professional_id = validProfIds.length === 1 ? validProfIds[0] : null;
  } else if (!formData.value.scheduling_professional_id && validProfIds.length === 1) {
    formData.value.scheduling_professional_id = validProfIds[0];
  }
};

const selectTimeSlot = time => {
  selectedTime.value = time;
};

function autoSetShiftFromTime(timeStr) {
  const hour = parseInt(timeStr.split(':')[0], 10);
  if (hour < 12) activeShift.value = 'morning';
  else if (hour < 18) activeShift.value = 'afternoon';
  else activeShift.value = 'night';
}

function getDayKeyFromDate(dateStr) {
  if (!dateStr) return 'monday';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dateObj = new Date(y, m - 1, d);
  const dayIndex = dateObj.getDay();
  const map = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return map[dayIndex] || 'monday';
}

const handleClickOutside = event => {
  if (patientContainerRef.value && !patientContainerRef.value.contains(event.target)) {
    showPatientDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const activeModalTab = ref('details'); // 'details' | 'reschedule'

// Package Creation Schedule by Day
const isCustomPackageSchedule = ref(false);
const packageScheduleByDay = ref({
  monday: { time_slot: '08:00', professional_id: null },
  tuesday: { time_slot: '08:00', professional_id: null },
  wednesday: { time_slot: '08:00', professional_id: null },
  thursday: { time_slot: '08:00', professional_id: null },
  friday: { time_slot: '08:00', professional_id: null },
  saturday: { time_slot: '08:00', professional_id: null },
  sunday: { time_slot: '08:00', professional_id: null },
});

// Reschedule Package State
const rescheduleFromDate = ref('');
const rescheduleNewDays = ref(['tuesday', 'thursday']);
const rescheduleNewTime = ref('08:00');
const rescheduleNewProfessionalId = ref(null);
const isCustomRescheduleByDay = ref(false);
const rescheduleScheduleByDay = ref({
  monday: { time_slot: '08:00', professional_id: null },
  tuesday: { time_slot: '08:00', professional_id: null },
  wednesday: { time_slot: '08:00', professional_id: null },
  thursday: { time_slot: '08:00', professional_id: null },
  friday: { time_slot: '08:00', professional_id: null },
  saturday: { time_slot: '08:00', professional_id: null },
  sunday: { time_slot: '08:00', professional_id: null },
});
const isRescheduling = ref(false);

const getDayLabel = key => {
  const d = availableWeekDays.find(w => w.key === key);
  return d ? d.full : key;
};

const toggleRescheduleDay = dayKey => {
  if (rescheduleNewDays.value.includes(dayKey)) {
    if (rescheduleNewDays.value.length > 1) {
      rescheduleNewDays.value = rescheduleNewDays.value.filter(d => d !== dayKey);
    }
  } else {
    rescheduleNewDays.value.push(dayKey);
  }
};

const handleReschedulePackage = async () => {
  if (!formData.value.id) return;
  errorMessage.value = '';
  isRescheduling.value = true;
  try {
    const payload = {
      from_date: rescheduleFromDate.value,
      new_recurrence_days: rescheduleNewDays.value,
      new_time_slot: rescheduleNewTime.value,
      new_professional_id: rescheduleNewProfessionalId.value || formData.value.scheduling_professional_id,
      new_service_id: formData.value.scheduling_service_id,
      schedule_by_day: isCustomRescheduleByDay.value ? rescheduleScheduleByDay.value : null,
    };
    await schedulingAPI.reschedulePackage(formData.value.id, payload);
    emit('saved');
    emit('close');
  } catch (err) {
    const errs = err.response?.data?.errors;
    errorMessage.value = Array.isArray(errs) ? errs.join(', ') : (err.response?.data?.error || err.response?.data?.message || 'Erro ao reagendar pacote.');
  } finally {
    isRescheduling.value = false;
  }
};

watch(
  () => props.appointment,
  app => {
    errorMessage.value = '';
    showPatientDropdown.value = false;
    isCreatingNewPatient.value = false;
    newPatientPhone.value = '';
    newPatientEmail.value = '';
    activeModalTab.value = 'details';

    if (app) {
      const appStart = new Date(app.start_time);
      const pad = n => String(n).padStart(2, '0');
      selectedDate.value = `${appStart.getFullYear()}-${pad(appStart.getMonth() + 1)}-${pad(appStart.getDate())}`;
      selectedTime.value = `${pad(appStart.getHours())}:${pad(appStart.getMinutes())}`;
      autoSetShiftFromTime(selectedTime.value);

      rescheduleFromDate.value = selectedDate.value;
      rescheduleNewTime.value = selectedTime.value;
      rescheduleNewProfessionalId.value = app.professional?.id || null;
      rescheduleNewDays.value = ['tuesday', 'thursday'];

      formData.value = {
        id: app.id,
        scheduling_patient_id: app.patient?.id || null,
        scheduling_service_id: app.service?.id || null,
        scheduling_professional_id: app.professional?.id || null,
        scheduling_partner_id: app.partner?.id || null,
        status: app.status || 'confirmed',
        session_type: app.session_type || 'single',
        session_number: app.session_number || 1,
        total_sessions: app.total_sessions || 1,
        price: app.price || 0,
        notes: app.notes || '',
        prontuario_notes: app.prontuario_notes || '',
      };
      patientSearchQuery.value = app.patient?.name || '';
    } else {
      const initDate = props.initialData?.start_time ? new Date(props.initialData.start_time) : new Date();
      const pad = n => String(n).padStart(2, '0');
      selectedDate.value = `${initDate.getFullYear()}-${pad(initDate.getMonth() + 1)}-${pad(initDate.getDate())}`;
      selectedTime.value = `${pad(initDate.getHours())}:${pad(initDate.getMinutes())}`;
      autoSetShiftFromTime(selectedTime.value);

      const sId = props.initialData?.service_id || (props.services[0]?.id || null);
      const srv = props.services.find(s => Number(s.id) === Number(sId));

      const initialDay = getDayKeyFromDate(selectedDate.value);
      const initPatientName = props.initialData?.patient_name || '';
      const initPatientPhone = props.initialData?.patient_phone || '';
      const initPatientEmail = props.initialData?.patient_email || '';

      if (props.initialData?.selectedDate) {
        selectedDate.value = props.initialData.selectedDate;
      }
      if (props.initialData?.selectedTime) {
        selectedTime.value = props.initialData.selectedTime;
        autoSetShiftFromTime(selectedTime.value);
      }

      formData.value = {
        id: null,
        scheduling_patient_id: props.initialData?.scheduling_patient_id || null,
        scheduling_service_id: sId,
        scheduling_professional_id: props.initialData?.scheduling_professional_id || props.initialData?.professional_id || (props.professionals[0]?.id || null),
        scheduling_partner_id: props.initialData?.scheduling_partner_id || null,
        conversation_id: props.initialData?.conversation_id || null,
        status: props.initialData?.status || 'confirmed',
        session_type: props.initialData?.session_type || 'single',
        session_number: props.initialData?.session_number || 1,
        total_sessions: props.initialData?.total_sessions || 10,
        price: props.initialData?.price !== undefined ? Number(props.initialData.price) : (srv?.price || 0),
        notes: props.initialData?.notes || '',
        prontuario_notes: props.initialData?.prontuario_notes || '',
      };
      patientSearchQuery.value = initPatientName;
      newPatientPhone.value = initPatientPhone;
      newPatientEmail.value = initPatientEmail;
      isCreatingNewPatient.value = !!(initPatientName && !props.initialData?.scheduling_patient_id);
      isCustomPackageSchedule.value = !!props.initialData?.isCustomPackageSchedule;
      isCustomRescheduleByDay.value = false;

      if (props.initialData?.selectedRecurrenceDays && Array.isArray(props.initialData.selectedRecurrenceDays)) {
        selectedRecurrenceDays.value = [...props.initialData.selectedRecurrenceDays];
      }

      if (props.initialData?.packageScheduleByDay) {
        packageScheduleByDay.value = { ...props.initialData.packageScheduleByDay };
      } else {
        availableWeekDays.forEach(d => {
          packageScheduleByDay.value[d.key] = {
            time_slot: selectedTime.value || '08:00',
            professional_id: formData.value.scheduling_professional_id,
          };
        });
      }

      availableWeekDays.forEach(d => {
        rescheduleScheduleByDay.value[d.key] = {
          time_slot: selectedTime.value || '08:00',
          professional_id: formData.value.scheduling_professional_id,
        };
      });
    }
  },
  { immediate: true }
);

const handleClose = () => {
  const currentDraft = {
    patient_name: patientSearchQuery.value,
    patient_phone: newPatientPhone.value,
    patient_email: newPatientEmail.value,
    scheduling_patient_id: formData.value.scheduling_patient_id,
    scheduling_service_id: formData.value.scheduling_service_id,
    service_id: formData.value.scheduling_service_id,
    scheduling_professional_id: formData.value.scheduling_professional_id,
    professional_id: formData.value.scheduling_professional_id,
    scheduling_partner_id: formData.value.scheduling_partner_id,
    selectedDate: selectedDate.value,
    selectedTime: selectedTime.value,
    session_type: formData.value.session_type,
    total_sessions: formData.value.total_sessions,
    price: formData.value.price,
    notes: formData.value.notes,
    prontuario_notes: formData.value.prontuario_notes,
    selectedRecurrenceDays: selectedRecurrenceDays.value,
    isCustomPackageSchedule: isCustomPackageSchedule.value,
    packageScheduleByDay: packageScheduleByDay.value,
    isDraft: true,
  };
  emit('close', currentDraft);
};

const saveAppointment = async () => {
  errorMessage.value = '';

  const patientNameTrimmed = patientSearchQuery.value.trim();
  if (!formData.value.scheduling_patient_id && !patientNameTrimmed) {
    errorMessage.value = 'Por favor, digite ou selecione o nome do paciente.';
    return;
  }
  if (!formData.value.scheduling_service_id) {
    errorMessage.value = 'Selecione o serviço.';
    return;
  }
  if (!formData.value.scheduling_professional_id && !isCustomPackageSchedule.value) {
    errorMessage.value = 'Selecione o profissional.';
    return;
  }
  if (!selectedDate.value || !selectedTime.value) {
    errorMessage.value = 'Informe a data e o horário de atendimento.';
    return;
  }

  isSaving.value = true;
  try {
    const startDateTimeStr = `${selectedDate.value}T${selectedTime.value}:00`;
    const [h, m] = selectedTime.value.split(':').map(Number);
    const endMinutes = h * 60 + m + Number(serviceDuration.value || 60);
    const endH = String(Math.floor(endMinutes / 60)).padStart(2, '0');
    const endM = String(endMinutes % 60).padStart(2, '0');
    const endDateTimeStr = `${selectedDate.value}T${endH}:${endM}:00`;

    const payload = {
      ...formData.value,
      recurrence_days: selectedRecurrenceDays.value,
      schedule_by_day: isCustomPackageSchedule.value ? packageScheduleByDay.value : null,
      patient_name: patientNameTrimmed,
      patient_phone: newPatientPhone.value.trim(),
      patient_email: newPatientEmail.value.trim(),
      start_time: startDateTimeStr,
      end_time: endDateTimeStr,
    };

    if (formData.value.id) {
      await schedulingAPI.updateAppointment(formData.value.id, payload);
    } else {
      await schedulingAPI.createAppointment(payload);
    }
    emit('saved');
    emit('close');
  } catch (err) {
    const errs = err.response?.data?.errors;
    const errText = err.response?.data?.error || (Array.isArray(errs) ? errs.join(', ') : err.response?.data?.message) || 'Erro ao salvar agendamento.';
    errorMessage.value = errText;
  } finally {
    isSaving.value = false;
  }
};

const deleteAppointment = async () => {
  if (!formData.value.id) return;

  const isPackage = formData.value.session_type === 'package';
  const confirmMsg = isPackage
    ? `Este agendamento faz parte de um pacote de ${formData.value.total_sessions} sessões. Deseja cancelar e excluir todas as sessões deste pacote e liberar as vagas na grade?`
    : 'Tem certeza que deseja cancelar e excluir este agendamento?';

  if (!confirm(confirmMsg)) return;

  isSaving.value = true;
  try {
    await schedulingAPI.deleteAppointment(formData.value.id);
    emit('saved');
    emit('close');
  } catch (err) {
    if (err.response?.status === 404) {
      emit('saved');
      emit('close');
    } else {
      errorMessage.value = err.response?.data?.error || err.response?.data?.message || 'Erro ao excluir agendamento.';
    }
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
    @click.self="handleClose"
  >
    <div
      class="w-full max-w-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden text-slate-800 dark:text-slate-100 flex flex-col max-h-[92vh]"
    >
      <!-- Header -->
      <div class="px-6 py-3.5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950/60">
        <div class="flex items-center gap-3">
          <h3 class="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <span>📅</span>
            <span>{{ formData.id ? 'Atendimento' : 'Novo Agendamento' }}</span>
            <span v-if="initialData?.isDraft" class="text-[10px] px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 font-semibold border border-amber-200 dark:border-amber-500/30">
              📝 Rascunho Restaurado
            </span>
          </h3>

          <!-- Tabs when editing a package appointment -->
          <div
            v-if="formData.id && formData.session_type === 'package'"
            class="flex items-center p-0.5 bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg ml-2"
          >
            <button
              type="button"
              class="px-2.5 py-1 text-xs font-bold rounded-md transition-all cursor-pointer"
              :class="
                activeModalTab === 'details'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              "
              @click="activeModalTab = 'details'"
            >
              📝 Detalhes da Sessão
            </button>
            <button
              type="button"
              class="px-2.5 py-1 text-xs font-bold rounded-md transition-all cursor-pointer"
              :class="
                activeModalTab === 'reschedule'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              "
              @click="activeModalTab = 'reschedule'"
            >
              🔁 Alterar Frequência do Pacote
            </button>
          </div>
        </div>
        <button type="button" class="text-slate-400 hover:text-slate-600 dark:hover:text-white cursor-pointer" @click="handleClose">✕</button>
      </div>

      <!-- Body: Reschedule Tab -->
      <div
        v-if="activeModalTab === 'reschedule' && formData.id"
        class="p-6 overflow-y-auto space-y-4 flex-1 animate-fade-in"
      >
        <div class="p-4 bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800/40 rounded-2xl space-y-2">
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
              <span>🔁</span> Remanejamento Inteligente de Frequência
            </span>
            <span class="text-[11px] font-bold px-2 py-0.5 bg-purple-100 dark:bg-purple-600/30 text-purple-700 dark:text-purple-200 rounded-full border border-purple-200 dark:border-purple-500/30">
              Sessão {{ formData.session_number }} de {{ formData.total_sessions }}
            </span>
          </div>
          <p class="text-xs text-purple-900/80 dark:text-purple-200/80 leading-relaxed">
            As sessões anteriores já concluídas com presença e prontuário serão mantidas intactas no histórico. Todas as sessões futuras a partir da data de corte serão redistribuídas nos novos dias da semana escolhidos, liberando as vagas antigas.
          </p>
        </div>

        <div v-if="errorMessage" class="p-3.5 text-xs bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300 rounded-xl flex items-start gap-2.5 shadow-xs">
          <span class="text-base shrink-0">⚠️</span>
          <span class="leading-relaxed font-medium">{{ errorMessage }}</span>
        </div>

        <!-- Start Date of New Frequency -->
        <div>
          <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Data de Início do Novo Cronograma <span class="text-rose-500 dark:text-red-400">*</span>
          </label>
          <input
            v-model="rescheduleFromDate"
            type="date"
            class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-medium focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        <!-- New Week Days Selector -->
        <div>
          <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
            Novos Dias da Semana de Atendimento: <span class="text-rose-500 dark:text-red-400">*</span>
          </label>
          <div class="grid grid-cols-7 gap-1.5">
            <button
              v-for="d in availableWeekDays"
              :key="d.key"
              type="button"
              class="py-2 text-xs font-bold rounded-xl border transition-all cursor-pointer text-center"
              :class="
                rescheduleNewDays.includes(d.key)
                  ? 'bg-purple-600 border-purple-500 text-white shadow-xs'
                  : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700/80 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-900 dark:hover:text-slate-200'
              "
              @click="toggleRescheduleDay(d.key)"
            >
              {{ d.label }}
            </button>
          </div>
        </div>

        <!-- Toggle: Horários Flexíveis por Dia no Remanejamento -->
        <div class="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 rounded-xl">
          <label class="flex items-center gap-2 cursor-pointer select-none">
            <input
              v-model="isCustomRescheduleByDay"
              type="checkbox"
              class="w-4 h-4 rounded text-purple-600 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 focus:ring-purple-500 cursor-pointer"
            />
            <span class="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Definir horários ou profissionais diferentes em cada dia
            </span>
          </label>
          <span class="text-[10px] text-purple-600 dark:text-purple-300 font-medium">
            {{ isCustomRescheduleByDay ? 'Modo Flexível' : 'Horário Padrão' }}
          </span>
        </div>

        <!-- Custom Schedule per Day on Reschedule -->
        <div v-if="isCustomRescheduleByDay" class="space-y-2.5 animate-fade-in">
          <div
            v-for="dayKey in rescheduleNewDays"
            :key="dayKey"
            class="p-3 bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-2xl space-y-2"
          >
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-purple-600 dark:text-purple-400 capitalize">
                🗓️ {{ getDayLabel(dayKey) }}
              </span>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">Horário</label>
                <input
                  v-model="rescheduleScheduleByDay[dayKey].time_slot"
                  type="text"
                  placeholder="08:00"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white font-mono text-center focus:ring-1 focus:ring-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">Profissional</label>
                <select
                  v-model="rescheduleScheduleByDay[dayKey].professional_id"
                  class="w-full h-8 px-2 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white cursor-pointer focus:ring-1 focus:ring-purple-500 focus:outline-none"
                >
                  <option v-for="prof in professionals" :key="prof.id" :value="prof.id">
                    {{ prof.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Standard New Time and Professional -->
        <div v-else class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Novo Horário de Atendimento <span class="text-rose-500 dark:text-red-400">*</span>
            </label>
            <input
              v-model="rescheduleNewTime"
              type="text"
              placeholder="08:00"
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono text-center focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Profissional Responsável
            </label>
            <select
              v-model="rescheduleNewProfessionalId"
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white cursor-pointer focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
            >
              <option v-for="prof in professionals" :key="prof.id" :value="prof.id">
                {{ prof.name }} ({{ prof.specialty || 'Geral' }})
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Body: Standard Details Tab -->
      <div
        v-else
        class="p-6 overflow-y-auto space-y-4 flex-1"
      >
        <div v-if="errorMessage" class="p-3.5 text-xs bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300 rounded-xl flex items-start gap-2.5 shadow-xs">
          <span class="text-base shrink-0">⚠️</span>
          <span class="leading-relaxed font-medium">{{ errorMessage }}</span>
        </div>

        <!-- 1. Patient Field with Autocomplete & Direct Creation -->
        <div ref="patientContainerRef" class="relative">
          <div class="flex items-center justify-between mb-1">
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Nome do Paciente <span class="text-rose-500 dark:text-red-400">*</span>
            </label>
            <span
              v-if="isCreatingNewPatient && !formData.scheduling_patient_id"
              class="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30"
            >
              Novo Paciente
            </span>
          </div>

          <div class="relative">
            <input
              v-model="patientSearchQuery"
              type="text"
              placeholder="Digite o nome do paciente..."
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
              autocomplete="off"
              @focus="showPatientDropdown = true"
              @input="handlePatientInput"
            />
            <span v-if="formData.scheduling_patient_id" class="absolute right-3 top-2.5 text-xs text-emerald-600 dark:text-emerald-400 font-bold">
              ✓ Cadastrado
            </span>
          </div>

          <!-- Dropdown Suggestions (Fecha ao clicar fora) -->
          <div
            v-if="showPatientDropdown && (filteredPatients.length > 0 || patientSearchQuery.trim())"
            class="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl z-50 max-h-48 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800"
          >
            <div
              v-for="p in filteredPatients"
              :key="p.id"
              class="p-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors flex items-center justify-between"
              @click="selectPatient(p)"
            >
              <div>
                <span class="text-xs font-bold text-slate-900 dark:text-white block">{{ p.name }}</span>
                <span class="text-[10px] text-slate-500 dark:text-slate-400">{{ p.phone_number || p.email || 'Sem contato' }}</span>
              </div>
              <span class="text-[10px] text-blue-600 dark:text-blue-400 font-medium">Selecionar</span>
            </div>

            <!-- Option to create new if query does not match exactly -->
            <div
              v-if="patientSearchQuery.trim() && !exactPatientMatch"
              class="p-2.5 bg-blue-50 dark:bg-blue-600/10 hover:bg-blue-100 dark:hover:bg-blue-600/20 cursor-pointer transition-colors flex items-center justify-between text-blue-700 dark:text-blue-300"
              @click="handleQuickCreatePatient"
            >
              <span class="text-xs font-bold flex items-center gap-1">
                <span>+</span> Cadastrar paciente: <strong>"{{ patientSearchQuery }}"</strong>
              </span>
              <span class="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold">
                {{ isQuickCreating ? 'Cadastrando...' : 'Criar Agora' }}
              </span>
            </div>
          </div>

          <!-- Optional Phone & Email for New Patient -->
          <div
            v-if="isCreatingNewPatient && !formData.scheduling_patient_id"
            class="mt-3 p-3 bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-xl space-y-2.5 animate-fade-in"
          >
            <div class="flex items-center justify-between">
              <span class="text-[11px] font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                <span>👤</span> Dados do novo paciente:
              </span>
              <button
                type="button"
                class="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 px-2 py-0.5 rounded-md cursor-pointer"
                :disabled="isQuickCreating"
                @click="handleQuickCreatePatient"
              >
                {{ isQuickCreating ? 'Cadastrando...' : '✓ Confirmar Cadastro' }}
              </button>
            </div>
            <div class="grid grid-cols-2 gap-2.5">
              <div>
                <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">WhatsApp / Telefone</label>
                <input
                  v-model="newPatientPhone"
                  type="text"
                  placeholder="+55 11 99999-9999"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
                />
              </div>
              <div>
                <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">E-mail</label>
                <input
                  v-model="newPatientEmail"
                  type="email"
                  placeholder="paciente@email.com"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 2. Service & Professional Selection -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Serviço <span class="text-rose-500 dark:text-red-400">*</span>
            </label>
            <select
              v-model="formData.scheduling_service_id"
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-slate-900 dark:text-white cursor-pointer"
              @change="onServiceChange"
            >
              <option :value="null">Selecione o serviço...</option>
              <option v-for="s in services" :key="s.id" :value="s.id">
                {{ s.name }} ({{ s.attendance_type === 'group' ? '👥 Grupo' : '👤 Particular' }} - {{ s.duration_minutes }} min)
              </option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Profissional <span class="text-rose-500 dark:text-red-400">*</span>
            </label>
            <select
              v-model="formData.scheduling_professional_id"
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-slate-900 dark:text-white cursor-pointer"
            >
              <option :value="null">Selecione o profissional...</option>
              <option v-for="prof in filteredProfessionals" :key="prof.id" :value="prof.id">
                {{ prof.name }} ({{ prof.specialty || 'Geral' }})
              </option>
            </select>
            <p v-if="formData.scheduling_service_id && filteredProfessionals.length === 0" class="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
              ⚠️ Nenhum profissional habilitado para este serviço.
            </p>
          </div>
        </div>

        <!-- 3. DATE & TIME PICKER (Design Limpo e Horários de 1 em 1 hora) -->
        <div class="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 space-y-3.5">
          <!-- Date Input & Friendly Display -->
          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Data do Atendimento <span class="text-rose-500 dark:text-red-400">*</span>
              </label>
              <span v-if="formattedDateDisplay" class="text-[11px] font-semibold text-blue-600 dark:text-blue-400 capitalize">
                {{ formattedDateDisplay }}
              </span>
            </div>
            <input
              v-model="selectedDate"
              type="date"
              class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl focus:ring-1 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white cursor-pointer"
            />
          </div>

          <!-- Time Selector with Shifts (Manhã, Tarde, Noite) & Quick Chips de 1 em 1 hora -->
          <div class="space-y-2 pt-1 border-t border-slate-200 dark:border-slate-800/80">
            <div class="flex items-center justify-between">
              <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Horário de Início <span class="text-rose-500 dark:text-red-400">*</span>
              </label>

              <!-- Shift Tabs -->
              <div class="flex items-center gap-1 p-0.5 bg-slate-200/60 dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-lg">
                <button
                  type="button"
                  class="px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                  :class="activeShift === 'morning' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
                  @click="activeShift = 'morning'"
                >
                  🌅 Manhã
                </button>
                <button
                  type="button"
                  class="px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                  :class="activeShift === 'afternoon' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
                  @click="activeShift = 'afternoon'"
                >
                  ☀️ Tarde
                </button>
                <button
                  type="button"
                  class="px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                  :class="activeShift === 'night' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
                  @click="activeShift = 'night'"
                >
                  🌙 Noite
                </button>
              </div>
            </div>

            <!-- Time Slots Grid Chips (1 em 1 hora) -->
            <div class="grid grid-cols-3 sm:grid-cols-6 gap-1.5 p-0.5">
              <button
                v-for="t in shiftTimes"
                :key="t"
                type="button"
                class="py-2 px-2 text-xs font-mono font-bold rounded-xl border transition-all text-center"
                :class="[
                  !isTimeSlotAllowedForService(t)
                    ? 'border-slate-200 dark:border-slate-800/80 bg-slate-100 dark:bg-slate-950/50 text-slate-400 dark:text-slate-600 opacity-40 cursor-not-allowed'
                    : (selectedTime === t
                      ? 'bg-blue-600 border-blue-500 text-white shadow-md cursor-pointer'
                      : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 text-slate-700 dark:text-slate-300 cursor-pointer')
                ]"
                :disabled="!isTimeSlotAllowedForService(t)"
                :title="!isTimeSlotAllowedForService(t) ? 'Horário fora da escala deste serviço' : t"
                @click="selectTimeSlot(t)"
              >
                {{ t }}
              </button>
            </div>

            <!-- Custom Manual Time Option + End Time Preview -->
            <div class="flex items-center justify-between pt-2 text-xs text-slate-500 dark:text-slate-400">
              <div class="flex items-center gap-1.5">
                <span class="text-[11px]">Outro horário:</span>
                <input
                  v-model="selectedTime"
                  type="time"
                  class="h-7 px-2 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white font-mono"
                  @input="autoSetShiftFromTime(selectedTime)"
                />
              </div>

              <!-- Computed End Time Summary -->
              <div v-if="computedEndTimeString" class="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                <span>⏱</span>
                <span>Término: {{ computedEndTimeString }} ({{ serviceDuration }} min)</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Checkbox de Aula Experimental (Aparece se o serviço permitir) -->
        <div
          v-if="allowsTrialClass && !formData.id"
          class="p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl flex items-center justify-between animate-fade-in"
        >
          <label class="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              v-model="isTrialClass"
              type="checkbox"
              class="w-4 h-4 rounded text-amber-500 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 focus:ring-amber-400 cursor-pointer"
              @change="onToggleTrialClass"
            />
            <div>
              <span class="text-xs font-bold text-amber-700 dark:text-amber-300 flex items-center gap-1">
                <span>⭐</span> Aula Experimental
              </span>
              <span class="text-[10px] text-slate-500 dark:text-slate-400 block">
                Trava o agendamento como atendimento avulso/teste
              </span>
            </div>
          </label>
          <span v-if="isTrialClass" class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/40">
            Atendimento Único
          </span>
        </div>

        <!-- 4. Session Type (Mensalidade Recorrente vs Pacote Finito vs Sessão Avulsa) -->
        <div v-if="!isTrialClass">
          <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
            Modalidade do Atendimento <span class="text-rose-500 dark:text-red-400">*</span>
          </label>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              type="button"
              class="py-2.5 px-3 rounded-xl border text-left text-xs font-bold transition-all flex flex-col gap-0.5 cursor-pointer"
              :class="
                formData.session_type === 'recurring'
                  ? 'bg-amber-50 dark:bg-amber-600/20 border-amber-500 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500 shadow-xs'
                  : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              "
              @click="formData.session_type = 'recurring'"
            >
              <div class="flex items-center gap-1.5 font-bold text-xs">
                <span>🧘‍♀️</span>
                <span>Mensalidade</span>
              </div>
              <span class="text-[10px] font-normal text-slate-500 dark:text-slate-400">Vaga fixa perpétua na grade</span>
            </button>

            <button
              type="button"
              class="py-2.5 px-3 rounded-xl border text-left text-xs font-bold transition-all flex flex-col gap-0.5 cursor-pointer"
              :class="
                formData.session_type === 'package'
                  ? 'bg-cyan-50 dark:bg-cyan-600/20 border-cyan-500 text-cyan-700 dark:text-cyan-300 ring-1 ring-cyan-500 shadow-xs'
                  : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              "
              @click="formData.session_type = 'package'"
            >
              <div class="flex items-center gap-1.5 font-bold text-xs">
                <span>🩺</span>
                <span>Pacote de Sessões</span>
              </div>
              <span class="text-[10px] font-normal text-slate-500 dark:text-slate-400">Total fixo (ex: 10 sessões)</span>
            </button>

            <button
              type="button"
              class="py-2.5 px-3 rounded-xl border text-left text-xs font-bold transition-all flex flex-col gap-0.5 cursor-pointer"
              :class="
                formData.session_type === 'single'
                  ? 'bg-blue-50 dark:bg-blue-600/20 border-blue-500 text-blue-700 dark:text-blue-300 ring-1 ring-blue-500 shadow-xs'
                  : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              "
              @click="formData.session_type = 'single'"
            >
              <div class="flex items-center gap-1.5 font-bold text-xs">
                <span>⚡</span>
                <span>Sessão Avulsa</span>
              </div>
              <span class="text-[10px] font-normal text-slate-500 dark:text-slate-400">1 atendimento pontual</span>
            </button>
          </div>
        </div>

        <!-- Botão de Ação Rápida de Presença (quando editando atendimento existente) -->
        <div
          v-if="formData.id"
          class="p-3 bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-xl flex items-center justify-between"
        >
          <div>
            <span class="text-xs font-bold text-slate-900 dark:text-white block">Presença do Paciente</span>
            <span class="text-[10px] text-slate-500 dark:text-slate-400">
              {{ formData.status === 'completed' ? 'Presença confirmada e registrada.' : 'Clique para marcar presença neste atendimento.' }}
            </span>
          </div>
          <button
            type="button"
            class="px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer"
            :class="
              formData.status === 'completed'
                ? 'bg-emerald-50 dark:bg-emerald-600/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/50'
                : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-xs'
            "
            @click="formData.status = formData.status === 'completed' ? 'confirmed' : 'completed'"
          >
            <span>{{ formData.status === 'completed' ? '✓ Presença Confirmada' : '✓ Confirmar Presença' }}</span>
          </button>
        </div>

        <!-- 5. CONFIGURADOR DE MENSALIDADE RECORRENTE (PILATES / ASSINATURA) -->
        <div
          v-if="formData.session_type === 'recurring'"
          class="p-4 bg-slate-50 dark:bg-slate-950/90 rounded-2xl border border-amber-200 dark:border-amber-500/30 space-y-3.5 animate-fade-in"
        >
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
              <span>🧘‍♀️</span> Matrícula Recorrente & Vaga Fixa
            </span>
            <span class="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
              Renovação mensal contínua
            </span>
          </div>

          <!-- Dia de Vencimento da Mensalidade -->
          <div>
            <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              Dia de Vencimento da Mensalidade:
            </label>
            <div class="flex flex-wrap items-center gap-1.5">
              <button
                v-for="d in [5, 10, 15, 20, 25]"
                :key="d"
                type="button"
                class="px-2.5 py-1 text-xs font-bold font-mono rounded-lg border transition-all cursor-pointer"
                :class="
                  formData.due_day === d
                    ? 'bg-amber-600 border-amber-500 text-white shadow-xs'
                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                "
                @click="formData.due_day = d"
              >
                Dia {{ String(d).padStart(2, '0') }}
              </button>
              <div class="flex items-center gap-1 ml-auto">
                <span class="text-[10px] text-slate-500 dark:text-slate-400">Outro:</span>
                <input
                  v-model.number="formData.due_day"
                  type="number"
                  min="1"
                  max="31"
                  class="w-12 h-7 px-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white font-mono text-center focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
            </div>
          </div>

          <!-- Week Days Selector -->
          <div v-if="!formData.id">
            <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              Dias Fixos da Semana:
            </label>
            <div class="grid grid-cols-7 gap-1.5">
              <button
                v-for="d in availableWeekDays"
                :key="d.key"
                type="button"
                class="py-2 text-xs font-bold rounded-xl border transition-all cursor-pointer text-center"
                :class="
                  selectedRecurrenceDays.includes(d.key)
                    ? 'bg-amber-600 border-amber-500 text-white shadow-xs'
                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700/80 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-900 dark:hover:text-slate-200'
                "
                @click="toggleRecurrenceDay(d.key)"
              >
                {{ d.label }}
              </button>
            </div>
          </div>

          <!-- Preview Informativo -->
          <div v-if="!formData.id && recurrencePreviewText" class="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 text-[11px] text-amber-800 dark:text-amber-300 leading-relaxed">
            💡 {{ recurrencePreviewText }}
          </div>
        </div>

        <!-- 6. CONFIGURADOR DE PACOTE FINITO (FISIOTERAPIA / REABILITAÇÃO) -->
        <div
          v-if="formData.session_type === 'package'"
          class="p-4 bg-slate-50 dark:bg-slate-950/90 rounded-2xl border border-cyan-200 dark:border-cyan-500/30 space-y-3.5 animate-fade-in"
        >
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-cyan-700 dark:text-cyan-300 flex items-center gap-1.5">
              <span>🩺</span> Configuração do Pacote de Sessões
            </span>
            <span class="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
              Controle até a Alta Clínica
            </span>
          </div>

          <!-- Total Sessions and Current Session -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Quantidade Total de Sessões <span class="text-rose-500 dark:text-red-400">*</span>
              </label>
              <input
                v-model.number="formData.total_sessions"
                type="number"
                min="1"
                max="100"
                class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-bold focus:outline-none focus:ring-1 focus:ring-cyan-500"
              />
            </div>

            <div v-if="formData.id">
              <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">Sessão Atual</label>
              <input
                v-model.number="formData.session_number"
                type="number"
                min="1"
                class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
              />
            </div>
            <div v-else class="flex flex-col justify-center">
              <span class="text-[10px] text-slate-500 dark:text-slate-400">
                A 1ª sessão iniciará na data e horário selecionados acima.
              </span>
            </div>
          </div>

          <!-- Week Days Selector -->
          <div v-if="!formData.id">
            <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              Dias da Semana de Atendimento:
            </label>
            <div class="grid grid-cols-7 gap-1.5">
              <button
                v-for="d in availableWeekDays"
                :key="d.key"
                type="button"
                class="py-2 text-xs font-bold rounded-xl border transition-all cursor-pointer text-center"
                :class="
                  selectedRecurrenceDays.includes(d.key)
                    ? 'bg-cyan-600 border-cyan-500 text-white shadow-xs'
                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700/80 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-900 dark:hover:text-slate-200'
                "
                @click="toggleRecurrenceDay(d.key)"
              >
                {{ d.label }}
              </button>
            </div>
          </div>

          <!-- Toggle: Horários Flexíveis por Dia na Criação -->
          <div v-if="!formData.id" class="flex items-center justify-between p-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
            <label class="flex items-center gap-2 cursor-pointer select-none">
              <input
                v-model="isCustomPackageSchedule"
                type="checkbox"
                class="w-4 h-4 rounded text-blue-600 bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-700 focus:ring-blue-500 cursor-pointer"
              />
              <span class="text-xs font-semibold text-slate-700 dark:text-slate-300">
                Definir horários ou profissionais diferentes por dia
              </span>
            </label>
            <span class="text-[10px] text-slate-500 dark:text-slate-400">
              {{ isCustomPackageSchedule ? 'Modo Flexível' : 'Mesmo horário' }}
            </span>
          </div>

          <!-- Custom Schedule per Selected Day on Package Creation -->
          <div v-if="!formData.id && isCustomPackageSchedule" class="space-y-2 animate-fade-in">
            <div
              v-for="dayKey in selectedRecurrenceDays"
              :key="dayKey"
              class="p-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl space-y-1.5"
            >
              <div class="flex items-center justify-between">
                <span class="text-xs font-bold text-blue-600 dark:text-blue-400 capitalize">
                  🗓️ {{ getDayLabel(dayKey) }}
                </span>
              </div>
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">Horário</label>
                  <input
                    v-model="packageScheduleByDay[dayKey].time_slot"
                    type="text"
                    placeholder="08:00"
                    class="w-full h-8 px-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white font-mono text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">Profissional</label>
                  <select
                    v-model="packageScheduleByDay[dayKey].professional_id"
                    class="w-full h-8 px-2 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option v-for="prof in filteredProfessionals" :key="prof.id" :value="prof.id">
                      {{ prof.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Real-Time Generator Preview Alert -->
          <div v-if="!formData.id && !isCustomPackageSchedule && recurrencePreviewText" class="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800/60 text-[11px] text-blue-800 dark:text-blue-300 leading-relaxed">
            💡 {{ recurrencePreviewText }}
          </div>
        </div>

        <!-- 6. Prontuário / Evolução da Sessão -->
        <div>
          <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Evolução / Anotação do Prontuário nesta Sessão
          </label>
          <textarea
            v-model="formData.prontuario_notes"
            rows="3"
            placeholder="Procedimentos realizados, queixas apresentadas, orientações passadas ao paciente..."
            class="w-full p-2.5 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 resize-none"
          />
        </div>

        <!-- 7. Complementary Notes -->
        <div>
          <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Informações Complementares / Observações Internas
          </label>
          <input
            v-model="formData.notes"
            type="text"
            placeholder="Ex: Pagamento pendente, compareceu com acompanhante..."
            class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
          />
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="px-6 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex items-center justify-between">
        <button
          v-if="formData.id"
          type="button"
          class="text-xs font-medium text-rose-600 hover:text-rose-700 dark:text-red-400 dark:hover:text-red-300 cursor-pointer"
          @click="deleteAppointment"
        >
          Excluir Agendamento
        </button>
        <div v-else />

        <div class="flex items-center gap-2.5">
          <button type="button" class="px-4 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white cursor-pointer" @click="handleClose">
            Cancelar
          </button>
          
          <!-- Reschedule Confirm Button -->
          <button
            v-if="activeModalTab === 'reschedule' && formData.id"
            type="button"
            class="px-5 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-500 rounded-xl shadow-md cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
            :disabled="isRescheduling"
            @click="handleReschedulePackage"
          >
            <span>🔁</span>
            <span>{{ isRescheduling ? 'Remanejando...' : 'Confirmar Novo Cronograma' }}</span>
          </button>

          <!-- Standard Save Button -->
          <button
            v-else
            type="button"
            class="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl shadow-md cursor-pointer disabled:opacity-50"
            :disabled="isSaving"
            @click="saveAppointment"
          >
            {{ isSaving ? 'Salvando...' : (formData.session_type === 'package' && !formData.id ? `Salvar e Gerar ${formData.total_sessions || 1} Sessões` : 'Salvar Atendimento') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
